import { useState, useEffect, useMemo } from 'react'
import type { AppDrawerApp } from '@elsapiens/shell'
import { buildSiblingAppUrl } from '@elsapiens/utils'
import { getRootDomain } from '@elsapiens/utils'

interface RawWorkspaceApp {
  app_id: string
  name: string
  icon: string
  description: string
  url: string
  subdomain: string
  show_in_drawer: boolean
  sort_order: number
}

function resolveIconUrl(icon: string, cdnOrigin: string): string | undefined {
  if (!icon) return undefined
  if (/^https?:\/\//i.test(icon)) return icon
  return `${cdnOrigin.replace(/\/+$/, '')}/${icon.replace(/^\/+/, '')}`
}

/**
 * Fetches the workspace's enrolled apps from the elBooks backend
 * (which proxies to elauth's WorkspaceService.GetWorkspaceApps gRPC).
 * Mirrors elinventory's useEnrolledApps pattern but uses REST instead of GraphQL.
 *
 * Returns `fallbackApps` while loading, on error, or when the response is empty.
 */
export function useWorkspaceAppsFromBackend(fallbackApps: AppDrawerApp[]): AppDrawerApp[] {
  const [raw, setRaw] = useState<RawWorkspaceApp[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/v1/workspace/apps')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: RawWorkspaceApp[]) => {
        if (!cancelled) { setRaw(data); setLoaded(true) }
      })
      .catch(() => {
        if (!cancelled) { setLoaded(true) }
      })
    return () => { cancelled = true }
  }, [])

  return useMemo<AppDrawerApp[]>(() => {
    if (!loaded || !raw.length) return fallbackApps

    const protocol = window.location.protocol
    const rootDomain =
      getRootDomain(window.location.hostname) ??
      (import.meta.env.VITE_ROOT_DOMAIN as string | undefined)

    const cdnOrigin =
      (import.meta.env.VITE_CDN_URL as string | undefined) ??
      (rootDomain ? `${protocol}//registry.${rootDomain}` : '')

    return raw
      .filter((a) => a.show_in_drawer)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((a): AppDrawerApp | null => {
        const url = rootDomain
          ? buildSiblingAppUrl(a.subdomain ?? '', { rootDomain })
          : null
        if (!url) return null
        return {
          id: a.app_id,
          name: a.name,
          description: a.description || undefined,
          iconUrl: resolveIconUrl(a.icon, cdnOrigin),
          url,
        }
      })
      .filter((a): a is AppDrawerApp => a !== null)
  }, [raw, loaded, fallbackApps])
}
